/* eslint-disable sort-keys-fix/sort-keys-fix */
import React, { useRef, useState } from 'react';
import { shape, string } from 'prop-types';
import * as R from 'ramda';

// Components
import { Box, ButtonBase, Menu, MenuItem, Typography, useMediaQuery } from '@mui/material';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import { useTheme } from '@mui/material/styles';
import ListTabs from '../list-tabs/ListTabs';
import StackedBarChart from '../stacked-bar-chart/StackedBarChart';

const labels = {
  daily: 'Next 7 Days',
  hourly: 'Next 24 Hours',
};

const ChartSection = props => {
  const { lastLocationDetailsFetchTime, locationDetails, locationLoadTimes } = props;
  const theme = useTheme();
  const lgUp = useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true });

  const [activeTab, setActiveTab] = useState('hourly');
  const [chartTypeMenuOpen, setChartTypeMenuOpen] = useState(false);

  const chartTabs = [
    { id: 'hourly', name: 'Next 24 Hours', shortName: 'Hourly' },
    { id: 'daily', name: 'Next 7 Days', shortName: 'Daily' },
  ];

  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getBarSize = () => {
    if (activeTab === 'hourly' && !lgUp) {
      return 7;
    }
    if (activeTab === 'daily' && !lgUp) {
      return 25;
    }
    if (activeTab === 'daily') {
      return 60;
    }
    return 15;
  };

  const formattedAverageLoadTime = () => {
    // If not returning locationLoadTimes, prevents crashing
    const formattedData = !R.isEmpty(locationLoadTimes)
      ? R.zipObj(daysOfWeek, locationLoadTimes?.lastFourWeeksDailyAverages)
      : R.zipWith(daysOfWeek, R.always(null));
    const updatedCurrentDayCentral = locationDetails?.dailyWaitTimes[0]?.key.slice(0, 3);
    const averageLoadTimeForChart = R.prop(updatedCurrentDayCentral)(formattedData);
    return averageLoadTimeForChart;
  };

  const stackedBarChartConfig = [
    {
      barFillColor: theme.palette.secondary.main,
      dataKey: 'currentWaitTime',
      stackId: 'waitTime',
    },
    {
      barFillColor: theme.palette.secondary.light,
      dataKey: 'averageWaitTime',
      stackId: 'waitTime',
    },
  ];

  const handleClick = type => {
    setActiveTab(type);
  };

  const menuAnchorRef = useRef(null);

  const handleMenuOpen = () => {
    setChartTypeMenuOpen(!chartTypeMenuOpen);
  };

  const handleHourlyButtonOnClick = () => {
    handleClick('hourly');
    handleMenuOpen();
  };
  const handleDailyButtonOnClick = () => {
    handleClick('daily');
    handleMenuOpen();
  };

  return (
    <Box
      sx={{
        backgroundColor: 'surface.white',
        boxShadow: { xs: 'unset', md: 5 },
        height: { xs: '100%', lg: 'initial' },
        marginTop: { xs: 0, md: 3 },
        pb: { xs: 0, md: 2 },
        pt: 2,
        px: 2,
        width: '100%',
      }}
    >
      {!lgUp ? (
        <Box alignItems="center" display="flex" justifyContent="space-between" mb={2}>
          <ButtonBase onClick={handleMenuOpen} ref={menuAnchorRef}>
            <Typography sx={{ color: 'primary.dark' }} variant="body2">
              {labels[activeTab]}
            </Typography>
            {chartTypeMenuOpen ? (
              <ArrowDropUpRoundedIcon sx={{ color: 'primary.dark' }} />
            ) : (
              <ArrowDropDownRoundedIcon sx={{ color: 'primary.dark' }} />
            )}
          </ButtonBase>
          <Menu anchorEl={menuAnchorRef?.current} onClose={handleMenuOpen} open={chartTypeMenuOpen}>
            <MenuItem onClick={handleHourlyButtonOnClick}>{labels.hourly}</MenuItem>
            <MenuItem onClick={handleDailyButtonOnClick}>{labels.daily}</MenuItem>
          </Menu>
          <Typography variant="caption">{`Last Updated: ${lastLocationDetailsFetchTime} `}</Typography>
        </Box>
      ) : (
        <Box mb={3} ml={2} mr={0} mt={2}>
          <Typography variant="h6">Average Wait Times</Typography>
          <Typography variant="caption">{`Last Updated: ${lastLocationDetailsFetchTime} `}</Typography>
        </Box>
      )}
      {lgUp && (
        <Box ml={2}>
          <ListTabs
            activeTab={activeTab}
            onChange={setActiveTab}
            pendingState={false}
            tabs={chartTabs}
          />
        </Box>
      )}
      {(!R.isEmpty(locationDetails) || !R.isEmpty(locationLoadTimes)) && (
        <StackedBarChart
          averageLoadTime={formattedAverageLoadTime()}
          barSize={getBarSize()}
          chartType={activeTab}
          config={stackedBarChartConfig}
          locationDetails={locationDetails}
        />
      )}
      {/* GRAPH KEY CONTAINER */}
      <Box
        alignItems="center"
        display="flex"
        justifyContent="center"
        marginBottom={1}
        marginTop={1}
        paddingBottom={3}
      >
        <Box alignItems="center" display="flex" marginRight={3}>
          <Box bgcolor="secondary.main" borderRadius="50%" height={15} marginRight={1} width={15} />
          <Typography stlye={{ fontWeight: 400 }} variant="caption">
            Current Wait
          </Typography>
        </Box>
        <Box alignItems="center" display="flex" marginRight={3}>
          <Box
            bgcolor="secondary.light"
            borderRadius="50%"
            height={15}
            marginRight={1}
            width={15}
          />
          <Typography stlye={{ fontWeight: 400 }} variant="caption">
            Avg Wait
          </Typography>
        </Box>
        <Box alignItems="center" display="flex">
          <Box bgcolor="success.dark" height={2} marginRight={1} width={15} />
          <Typography stlye={{ color: 'rgba(0, 0, 0, 0.38)', fontWeight: 400 }} variant="caption">
            Avg Load Time
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

ChartSection.propTypes = {
  lastLocationDetailsFetchTime: string.isRequired,
  locationDetails: shape({}).isRequired,
  locationLoadTimes: shape({}),
};

ChartSection.defaultProps = {
  locationLoadTimes: {},
};

export default ChartSection;
